import React, {PureComponent} from "react"
import {cloneDeep} from "lodash"

import {Wallet} from "@irati/types"
import {connect} from "@irati/store"
import Loading from "@irati/ui/src/Loading"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import statementItemActions from "@irati/wallet/store/actions/StatementItem"
import {ReduxState} from "@irati/store/types"
import DateGroupedStatementItems from "@irati/wallet/types/DateGroupedStatementItems"

import NoData, {NoDataCTA} from "../NoData"
import StatementItemDetail from "./StatementItemDetail"

const PAGINATION_COUNT = 10

interface WalletStatementItem {
  results: DateGroupedStatementItems[]
}

interface Props extends InjectIntl {
  className?: string
  listStatementItems: (existingStatementItems: DateGroupedStatementItems[], wallet: Wallet, limit: number, cursor?: string) => Promise<any>
  clearStatementItems: () => Promise<any>
  wallet: Wallet
  walletStatementItem: WalletStatementItem
  isMobile: boolean
}

class StatementItems extends PureComponent<Props> {
  static getDerivedStateFromProps(props: Props) {
    const cloned = cloneDeep(props.walletStatementItem.results)
    const transactions =
      cloned &&
      cloned.filter((day) => {
        day.data = day.data.filter((t) => t.transfer.type !== "MonthlyTreatTokens")
        return day.data.length
      })

    return {transactions}
  }

  static defaultProps = {
    className: null
  }

  state = {
    load: null,
    canLoadMore: true,
    cursor: undefined,
    transactions: []
  }

  getMoreItems = (existingStatementItems: DateGroupedStatementItems[], cursor?: string) => {
    const {wallet} = this.props
    const list = this.props.listStatementItems(existingStatementItems, wallet, PAGINATION_COUNT, cursor).then(({newCursor}) => {
      this.setState({
        canLoadMore: true,
        cursor: newCursor
      })
    })
    this.setState({
      load: list,
      canLoadMore: false
    })
  }

  componentDidMount() {
    if (this.props.wallet.uuid) {
      this.getMoreItems([], null)
    }
  }

  componentWillUnmount() {
    this.props.clearStatementItems()
  }

  componentDidUpdate(nexProps: Props) {
    if (this.props.wallet.uuid !== nexProps.wallet.uuid) {
      this.getMoreItems([], null)
    }
  }

  handleShowMoreClick = () => {
    const {cursor} = this.state
    if (cursor) {
      const {results} = this.props.walletStatementItem
      this.getMoreItems(results, cursor)
    }
  }

  render() {
    const {
      className,
      walletStatementItem: {results},
      formatMessage,
      isMobile,
      wallet
    } = this.props
    const {cursor, load, canLoadMore, transactions} = this.state

    if (!results) {
      return <Loading load={this.state.load} />
    }

    const hasData = results.length > 0
    const idNamespace = id("statement-items", this.props)

    return (
      <div className={className} id={idNamespace}>
        {!transactions.length && !!wallet.availableBalance && wallet.currency === "TT" && (
          <NoData
            useCardStyle={isMobile}
            id={idNamespace}
            text={formatMessage({id: "wallet.noStatementItems.NoTreatTokensTransactionsText"})}>
            <NoDataCTA currency={wallet.currency} />
          </NoData>
        )}
        {hasData && (
          <StatementItemDetail
            results={results}
            paginationCount={PAGINATION_COUNT}
            onShowMoreClick={this.handleShowMoreClick}
            load={load}
            canLoadMore={canLoadMore}
            id={idNamespace}
            cursor={cursor}
          />
        )}
        {!hasData && wallet.currency !== "TT" && (
          <NoData useCardStyle={isMobile} id={idNamespace} text={formatMessage({id: "wallet.noStatementItems.text"})}>
            <NoDataCTA />
          </NoData>
        )}
      </div>
    )
  }
}

const mapState = ({walletStatementItem, mq}: ReduxState) => {
  return {
    walletStatementItem,
    isMobile: mq.lessThan.large
  }
}

const mapDispatch = {
  listStatementItems: (existingStatementItems: DateGroupedStatementItems[], wallet: Wallet, limit: number, cursor?: string) =>
    statementItemActions.list(existingStatementItems, wallet, limit, cursor),
  clearStatementItems: statementItemActions.clear
}

export default injectIntl(connect(mapState, mapDispatch)(StatementItems))
